import { Card } from '@/components/ui/primitives';

/* ============================================================================
 * Lane 2B — shared "choose a domain" gate for the SEO module pages.
 *
 * Shown when a workspace has MORE THAN ONE connected domain and none is the
 * active view yet. SEO data (keywords, rankings, competitors, audits, alerts,
 * opportunities …) is specific to each domain, so a page must NOT read against
 * an arbitrary first-domain default (the ORDER BY id ASC / options[0] trap in
 * multi-domain-audit-2026-07-19). Every domain-scoped SEO page renders this
 * chooser instead of silently assuming booksesh.com.
 *
 * Single-domain workspaces auto-select their sole domain in useActiveDomain()
 * (§2.2) and never see this gate — zero added friction.
 * ==========================================================================*/

export interface DomainChoiceOption {
  id: string;
  label: string;
}

export function DomainChoiceGate({
  options,
  onSelect,
  title = 'Choose a domain',
  teach = 'This workspace has more than one connected domain. SEO data is specific to each domain, so pick the one you want to work on — nothing is shown until you choose, and no domain is assumed for you.',
}: {
  options: ReadonlyArray<DomainChoiceOption>;
  onSelect: (id: string) => void;
  title?: string;
  teach?: string;
}) {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-base font-semibold text-text-primary">{title}</h2>
        <p className="mt-1 max-w-prose text-sm text-text-secondary">{teach}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(o.id)}
              className="min-h-[44px] rounded-md border border-border-default bg-canvas px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:border-ai hover:bg-hover focus:outline-none focus:ring-2 focus:ring-ai/30"
            >
              {o.label}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
